import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import type {
  AgentAddPostInput,
  AgentListPostsParams,
  AgentTogglePostStatusParams,
  AgentEditPostInput,
} from '../api'

import postService from './agent-post.service'
import type { IMutationArgs } from '../types'
import { EAgentPostQueryKey } from './agent-post.types'
import { EAgentProjectQueryKey } from '../agent-project'

export const useAgentAddPost = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: (data: { data: AgentAddPostInput; token: string }) =>
      postService.agentAddPost(data.data, data.token),
    onSuccess: (data) => {
      if (invalidate) {
        queryClient.invalidateQueries({
          queryKey: [EAgentProjectQueryKey.AGENT_PROJECT],
        })
        queryClient.invalidateQueries({
          queryKey: [EAgentProjectQueryKey.AGENT_LIST_AVAILABLE_PROJECTS],
        })
      }
      return data
    },
  })
}

export const useAgentListPosts = (
  params: AgentListPostsParams,
  enabled: boolean,
) => {
  return useInfiniteQuery({
    queryKey: [EAgentPostQueryKey.AGENT_LIST_POSTS, params],
    queryFn: ({ pageParam = 0 }) => {
      return postService.agentListPosts({
        ...params,
        skip: pageParam * (params.limit || 10),
      })
    },
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage.data?.total || 0
      const currentLength = allPages.reduce(
        (acc, page) => acc + (page.data.posts?.length || 0),
        0,
      )
      return currentLength < total ? allPages.length : undefined
    },
    initialPageParam: 0,
    enabled: enabled,
  })
}

export const useResetPosts = () => {
  const queryClient = useQueryClient()

  const resetAgentPosts = () => {
    queryClient.invalidateQueries({
      queryKey: [EAgentPostQueryKey.AGENT_LIST_POSTS],
    })
  }

  return {
    resetAgentPosts,
  }
}

export const useAgentTogglePostStatus = () => {
  return useMutation({
    mutationFn: (data: AgentTogglePostStatusParams) =>
      postService.agentTogglePostStatus(data),
  })
}

export const useAgentUploadPostPhoto = () => {
  return useMutation({
    mutationFn: (formData: FormData) =>
      postService.agentUploadPostPhoto(formData),
  })
}

export const useAgentUploadPostDocument = () => {
  return useMutation({
    mutationFn: (formData: FormData) =>
      postService.agentUploadPostDocument(formData),
  })
}

export const useAgentDeletePostPhoto = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: ({ postId, photoId }: { postId: number; photoId: number }) =>
      postService.agentDeletePostPhoto(postId, photoId),
    onSuccess: (data) => {
      if (invalidate) {
        queryClient.invalidateQueries({
          queryKey: [EAgentPostQueryKey.AGENT_LIST_POSTS],
        })
      }
      return data
    },
  })
}

export const useAgentDeletePostDocument = () => {
  return useMutation({
    mutationFn: ({
      postId,
      documentId,
    }: {
      postId: number
      documentId: number
    }) => postService.agentDeletePostDocument(postId, documentId),
  })
}

export const useAgentEditPost = (args: IMutationArgs) => {
  const queryClient = useQueryClient()
  const { invalidate } = args
  return useMutation({
    mutationFn: ({
      token,
      postId,
      data,
    }: {
      token: string
      postId: number
      data: AgentEditPostInput
    }) => postService.agentEditPost({ postId, token }, data),
    onSuccess: (data) => {
      if (invalidate) {
        queryClient.invalidateQueries({
          queryKey: [EAgentPostQueryKey.AGENT_LIST_POSTS],
        })
        queryClient.invalidateQueries({
          queryKey: [EAgentProjectQueryKey.AGENT_PROJECT],
        })
        queryClient.invalidateQueries({
          queryKey: [EAgentProjectQueryKey.AGENT_LIST_AVAILABLE_PROJECTS],
        })
      }
      return data
    },
  })
}
